import { Box, Card, CardContent, CardHeader, Grid, Paper, Tabs, Tab, Link, Typography } from "@mui/material";
import CumulativePatientsPlot from "./components/plots/CumulativePatientsPlot";
import NewPatientsPlot from "./components/plots/NewPatientsPlot";
import ScheduleBasedGapsPlot from "./components/plots/ScheduleBasedGapsPlot";
import TherapistCollapsibleTable from "./components/tables/TherapistTable";
import useNewPatientsStats from "./hooks/PatientStats/useNewPatientsStats";
import useCumulativePatientsStats from "./hooks/PatientStats/useCumulativePatientsStats";
import { useEffect, useState } from "react";
import useStore from "../../helpers/useStore";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import useAllRxCountStats from "./hooks/RxStats/useAllRxCountStats";
import useAllRxValueStats from "./hooks/RxStats/useAllRxValueStats";
import useRxTherapistCountStats from "./hooks/RxStats/useRxTherapistCountStats";
import useRxLifespanDistributionStats from "./hooks/RxStats/useRxLifespanDistributionStats";
import useTotalAppointmentValueStats from "./hooks/ScheduleBasedStats/useTotalAppointmentValueStats";
import useScheduleAllRxCountStats from "./hooks/ScheduleBasedStats/useScheduleAllRxCountStats";
import useTotalGapsStats from "./hooks/ScheduleBasedStats/useTotalGapsStats";
import useTherapyRxFrequencyStats from "./hooks/ScheduleBasedStats/useTherapyRxFrequencyStats";
import RxCountsPlot from "./components/plots/RxCountsPlot";
import useTherapistStats from "./hooks/TherapistStats/useTherapistStats";
import ScheduleBasedApptValuesPlot from "./components/plots/ScheduleBasedApptValuesPlot";
import BinTypeSelector from "./components/BinTypeSelector";
import BIDateRangePicker from "./components/DateRangePicker";
import "./components/BIDashboard.css";
import LoadingSpinner from "./components/LoadingSpinner";
import PlotSelector from "./components/PlotSelector";
import TimeframePagination from "./components/TimeframePagination";
import { getNextMonth, getPreviousMonth } from "./helpers/dateNavigation";
import { transformRxTherapistsStats, transformRxLifespanData } from "./components/tableTransforms";
import SummaryStatsTable from "./components/tables/SummaryStatsTable";
import { calculateSummaryStats, calculateTherapistPerRxSummaryStats, calculateRxLifespanSummaryStats } from "./helpers/summaryStats";
import TherapistStatsTotals from "./components/TherapistStatsTotals";
import PlotDataTable from "./components/tables/PlotDataTable";
import { plotDataTableConfigs } from "./configs/plotDataTableConfigs";
import TableViewSelector from "./components/TableViewSelector";
import ExportButton from "./components/ExportButton";
import { summaryStatsTableConfigs } from "./configs/summaryStatsTableConfigs";
import useMonthlyRevenue from "./hooks/MonthlyRevenue/useMonthlyRevenue";
import MonthlyRevenuePlot from "./components/plots/MonthlyRevenueEvolutionPlot";
import ScheduleBasedTherapyRxPlot from "./components/plots/ScheduleBasedTherapyRxPlot";
import RxValuesPlot from "./components/plots/RxValuesPlot";
import SwitchView from "./components/SwitchView";
import LocationSelector from "./components/LocationSelector";
import {
  patientStatsDownloadConfig,
  rxStatsDownloadConfig,
  scheduleStatsDownloadConfig,
  therapistStatsDownloadConfig,
  monthlyRevenueDownloadConfig,
  rxBlankoStatsDownloadConfig,
  yearEndRxUnfinishedValuesDownloadConfig,
} from "./configs/downloadConfigs";
import RxTherapistCountsPlot from "./components/plots/RxTherapistCountsPlot";
import RxLifespanPlot from "./components/plots/RxLifespanPlot";
import RxLifespanTimeDeltaSelector from "./components/RxLifespanTimeDeltaSelector";
import TherapistCountFilter from "./components/TherapistCountFilter";
import InsuranceAnalyticsContainer from "../Transactions/Insurance/Base/components/analytics/InsuranceAnalyticContainer";
import useBIDashboardState from "./hooks/useBIDashboardState";
import useLocations from "./hooks/useLocations";
import { setColorDomain } from "./helpers/locationColors";
import RxBlankoSwitch from "./components/RxBlankoSwitch";
import useRxBlankoCounts from "./hooks/RxStats/useRxBlankoCounts";
import useRxBlankoValues from "./hooks/RxStats/useRxBlankoValues";
import useRxBlankoLifespan from "./hooks/RxStats/useRxBlankoLifespan";
import useRxBlankoWithTherapistsCounts from "./hooks/RxStats/useRxBlankoWithTherapistsCounts";
import YearEndRxUnfinishedValueTable from "./components/tables/YearEndRxUnfinishedValuesTable";
import { yearEndRxUnfinishedValuesTableConfigs } from "./configs/yearEndRxUnfinishedValuesTableConfigs";
import useYearEndRxUnfinishedValues from "./hooks/YearEndStats/useYearEndRxUnfinishedValues";
import { DatePicker } from "@mui/x-date-pickers";
import YearSelector from "./components/YearSelector";

export const BIDashboardPage = () => {
  const biDashboardState = useBIDashboardState();
  const { locations } = useLocations();
  const patientsStore = useStore(PatientsContext);

  const [selectedTab, setSelectedTab] = useState<number>(0);

  // Patient Stats - separate hooks for each plot type
  const {
    patientCountResults,
    isLoading: isNewPatientsLoading,
    availableLocations: availableNewPatientLocations,
  } = useNewPatientsStats(
    biDashboardState.patientBinType,
    biDashboardState.patientStartDate,
    biDashboardState.patientEndDate,
    biDashboardState.patientLocations_newPatients
  );
  const {
    patientCumulativeResults,
    isLoading: isCumulativePatientsLoading,
    availableLocations: availableCumulativePatientLocations,
  } = useCumulativePatientsStats(
    biDashboardState.patientBinType,
    biDashboardState.patientStartDate,
    biDashboardState.patientEndDate,
    biDashboardState.patientLocations_cumulativePatients
  );

  // Rx Stats - separate hooks for each plot type
  const {
    totalRxCountResults,
    isLoading: isAllRxCountLoading,
    availableLocations: availableRxCountLocations,
  } = useAllRxCountStats(
    biDashboardState.rxBinType,
    biDashboardState.rxStartDate,
    biDashboardState.rxEndDate,
    biDashboardState.rxLocations_allRxCount,
    biDashboardState.rxBlankoFilter ? "Blankoverordnung" : undefined
  );
  const {
    totalRxValueResults,
    isLoading: isAllRxValueLoading,
    availableLocations: availableRxValueLocations,
  } = useAllRxValueStats(
    biDashboardState.rxBinType,
    biDashboardState.rxStartDate,
    biDashboardState.rxEndDate,
    biDashboardState.rxLocations_allRxValue,
    biDashboardState.rxBlankoFilter ? "Blankoverordnung" : undefined
  );
  const {
    rxTherapistsStats,
    filteredRxTherapistsStats,
    availableTherapistCounts,
    isLoading: isRxTherapistCountLoading,
    availableLocations: availableRxTherapistCountLocations,
  } = useRxTherapistCountStats(
    biDashboardState.rxStartDate,
    biDashboardState.rxEndDate,
    biDashboardState.rxLocations_rxTherapistCount,
    biDashboardState.therapistCountFilter,
    biDashboardState.rxBlankoFilter ? "Blankoverordnung" : undefined
  );
  const {
    rxLifespan,
    isLoading: isRxLifespanLoading,
    availableLocations: availableRxLifespanLocations,
  } = useRxLifespanDistributionStats(
    biDashboardState.rxStartDate,
    biDashboardState.rxEndDate,
    biDashboardState.rxLocations_rxLifespanDistribution,
    biDashboardState.rxBlankoFilter ? "Blankoverordnung" : undefined
  );

  // Rx Blanko Stats - separate hooks for each plot type
  const {
    totalRxBlankoCountResults,
    isLoading: isRxBlankoCountsLoading,
    availableLocations: availableRxBlankoCountLocations,
  } = useRxBlankoCounts(
    biDashboardState.rxBlankoBinType,
    biDashboardState.rxBlankoStartDate,
    biDashboardState.rxBlankoEndDate,
    biDashboardState.rxBlankoLocations_allRxBlankoCount
  );
  const {
    totalRxBlankoValueResults,
    isLoading: isRxBlankoValuesLoading,
    availableLocations: availableRxBlankoValueLocations,
  } = useRxBlankoValues(
    biDashboardState.rxBlankoBinType,
    biDashboardState.rxBlankoStartDate,
    biDashboardState.rxBlankoEndDate,
    biDashboardState.rxBlankoLocations_allRxBlankoValue
  );
  const {
    rxBlankoWithTherapists,
    filteredRxBlankoWithTherapists,
    availableTherapistCounts: availableRxBlankoTherapistCounts,
    isLoading: isRxBlankoTherapistLoading,
    availableLocations: availableRxBlankoTherapistCountLocations,
  } = useRxBlankoWithTherapistsCounts(
    biDashboardState.rxBlankoStartDate,
    biDashboardState.rxBlankoEndDate,
    biDashboardState.rxBlankoLocations_rxBlankoTherapistCount,
    biDashboardState.therapistCountFilter
  );
  const {
    rxBlankoLifespan,
    isLoading: isRxBlankoLifespanLoading,
    availableLocations: availableRxBlankoLifespanLocations,
  } = useRxBlankoLifespan(
    biDashboardState.rxBlankoStartDate,
    biDashboardState.rxBlankoEndDate,
    biDashboardState.rxBlankoLocations_rxBlankoLifespanDistribution
  );

  // Schedule Stats - separate hooks for each plot type
  const {
    totalApptValueResults,
    isLoading: isTotalApptValueLoading,
    availableLocations: availableApptValueLocations,
  } = useTotalAppointmentValueStats(
    biDashboardState.scheduleBinType,
    biDashboardState.scheduleStartDate,
    biDashboardState.scheduleEndDate,
    biDashboardState.scheduleLocations_totalAppointmentValue
  );
  const {
    totalScheduleBasedRxCountResults,
    isLoading: isScheduleAllRxCountLoading,
    availableLocations: availableScheduleRxCountLocations,
  } = useScheduleAllRxCountStats(
    biDashboardState.scheduleBinType,
    biDashboardState.scheduleStartDate,
    biDashboardState.scheduleEndDate,
    biDashboardState.scheduleLocations_allRxCount
  );
  const {
    totalGapsResults,
    isLoading: isTotalGapsLoading,
    availableLocations: availableGapsLocations,
  } = useTotalGapsStats(
    biDashboardState.scheduleBinType,
    biDashboardState.scheduleStartDate,
    biDashboardState.scheduleEndDate,
    biDashboardState.scheduleLocations_totalGaps
  );
  const {
    therapyRxFrequency,
    isLoading: isTherapyRxFrequencyLoading,
    availableLocations: availableTherapyRxFrequencyLocations,
  } = useTherapyRxFrequencyStats(
    biDashboardState.scheduleStartDate,
    biDashboardState.scheduleEndDate,
    biDashboardState.scheduleLocations_therapyRxFrequency
  );

  // Therapist Stats
  const {
    therapistData,
    therapistTotalsData,
    flatTherapistData,
    isLoading: isTherapistLoading,
  } = useTherapistStats(biDashboardState.therapistStartDate, biDashboardState.therapistEndDate);

  // Estimated monthly revenue
  const {
    monthlyRevenue,
    isLoading: isMonthlyRevenueLoading,
    availableLocations: availableMonthlyRevenueLocations,
  } = useMonthlyRevenue(biDashboardState.revenueMonth, biDashboardState.monthlyRevenueLocations);

  // Year end stats
  const {
    yearEndRxUnfinishedValues,
    isLoading: isYearEndRxUnfinishedValuesLoading,
  } = useYearEndRxUnfinishedValues(biDashboardState.yearEndRxUnfinishedValuesStartDate, biDashboardState.yearEndRxUnfinishedValuesEndDate);

  // Transform Rx data to include clickable Rx# links
  const transformedRxTherapistsStats = transformRxTherapistsStats(filteredRxTherapistsStats, patientsStore);
  const transformedRxLifespan = transformRxLifespanData(rxLifespan, patientsStore);
  const transformedRxBlankoTherapistsStats = transformRxTherapistsStats(filteredRxBlankoWithTherapists, patientsStore);
  const transformedRxBlankoLifespan = transformRxLifespanData(rxBlankoLifespan, patientsStore);

  const dataMap = {
    newPatients: patientCountResults,
    cumulativePatients: patientCumulativeResults,
    allRxCount: totalRxCountResults,
    allRxValue: totalRxValueResults,
    rxTherapistCount: transformedRxTherapistsStats,
    rxTherapistCountOriginal: filteredRxTherapistsStats, // Original data for downloads
    rxLifespan: transformedRxLifespan,
    rxLifespanOriginal: rxLifespan, // Original data for downloads
    totalApptValue: totalApptValueResults,
    totalScheduleBasedRxCount: totalScheduleBasedRxCountResults,
    totalScheduleBasedGaps: totalGapsResults,
    therapyRxFrequency: therapyRxFrequency,
    estimatedMonthlyRevenue: monthlyRevenue,
    therapistTable: flatTherapistData,
    allRxBlankoCount: totalRxBlankoCountResults,
    allRxBlankoValue: totalRxBlankoValueResults,
    rxBlankoTherapistCount: transformedRxBlankoTherapistsStats,
    rxBlankoTherapistCountOriginal: rxBlankoWithTherapists, // Original data for downloads
    rxBlankoLifespan: transformedRxBlankoLifespan,
    rxBlankoLifespanOriginal: rxBlankoLifespan, // Original data for downloads
    yearEndRxUnfinishedValues: yearEndRxUnfinishedValues,
  };

  // Data download configurations
  const patientConfig = patientStatsDownloadConfig(biDashboardState.selectPatientPlot, biDashboardState.patientTable, dataMap);
  const rxConfig = rxStatsDownloadConfig(biDashboardState.selectRxPlot, biDashboardState.rxTable, dataMap);
  const scheduleConfig = scheduleStatsDownloadConfig(biDashboardState.selectScheduleBasedPlot, biDashboardState.scheduleTable, dataMap);
  const revenueConfig = monthlyRevenueDownloadConfig(biDashboardState.monthlyRevenueTable, dataMap);
  const therapistConfig = therapistStatsDownloadConfig(dataMap, biDashboardState.therapistStartDate, biDashboardState.therapistEndDate);
  const rxBlankoConfig = rxBlankoStatsDownloadConfig(biDashboardState.selectRxBlankoPlot, biDashboardState.rxBlankoTable, dataMap);
  const yearEndUnfinishedValsConfig = yearEndRxUnfinishedValuesDownloadConfig(dataMap);

  // once locations are available, initialize the color scale
  useEffect(() => {
    if (locations.length > 0) {
      setColorDomain(locations.map((loc) => loc.name));
    }
  }, [locations]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs sx={{ px: 1.2 }} value={selectedTab} onChange={handleTabChange} aria-label="dashboard tabs">
          <Tab label="Main-Controlling" value={0} />
          <Tab label="Abrechnungsstatistik" value={1} />
        </Tabs>
      </Box>
      <Box sx={{ pt: 1, height: "100%" }}>
        {selectedTab === 0 && (
          <Grid className="biDashboard" container spacing={2} sx={{ px: 1.2, height: "100%" }}>
            <Grid item xs={6}>
              <Card className="custom-card">
                <CardHeader title="Therapeuten-Statistik" />
                <CardContent sx={{ flexGrow: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 0 }}>
                    <Box sx={{ display: "flex", gap: 3 }}>
                      <TimeframePagination
                        onPrevious={() => {
                          const [prevStart, prevEnd] = getPreviousMonth(biDashboardState.therapistStartDate);
                          biDashboardState.setTherapistStartDate(prevStart);
                          biDashboardState.setTherapistEndDate(prevEnd);
                        }}
                        onNext={() => {
                          const [nextStart, nextEnd] = getNextMonth(biDashboardState.therapistStartDate);
                          biDashboardState.setTherapistStartDate(nextStart);
                          biDashboardState.setTherapistEndDate(nextEnd);
                        }}
                      />
                      <BIDateRangePicker
                        startDate={biDashboardState.therapistStartDate}
                        endDate={biDashboardState.therapistEndDate}
                        onChange={({ startDate, endDate }) => {
                          biDashboardState.setTherapistStartDate(startDate);
                          biDashboardState.setTherapistEndDate(endDate);
                        }}
                      />
                    </Box>
                    <ExportButton {...therapistConfig} startDate={biDashboardState.therapistStartDate} endDate={biDashboardState.therapistEndDate} />
                  </Box>
                  <TherapistStatsTotals therapistTotalsData={therapistTotalsData} isLoading={isTherapistLoading} />
                  <TherapistCollapsibleTable therapistData={therapistData} isLoading={isTherapistLoading} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card className="custom-card">
                <CardHeader title="Patienten-Statistik" />
                <CardContent sx={{ flexGrow: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", gap: 3 }}>
                      <TimeframePagination
                        onPrevious={() => {
                          const [prevStart, prevEnd] = getPreviousMonth(biDashboardState.patientStartDate);
                          biDashboardState.setPatientStartDate(prevStart);
                          biDashboardState.setPatientEndDate(prevEnd);
                        }}
                        onNext={() => {
                          const [nextStart, nextEnd] = getNextMonth(biDashboardState.patientStartDate);
                          biDashboardState.setPatientStartDate(nextStart);
                          biDashboardState.setPatientEndDate(nextEnd);
                        }}
                      />
                      <BIDateRangePicker
                        startDate={biDashboardState.patientStartDate}
                        endDate={biDashboardState.patientEndDate}
                        onChange={({ startDate, endDate }) => {
                          biDashboardState.setPatientStartDate(startDate);
                          biDashboardState.setPatientEndDate(endDate);
                        }}
                      />
                    </Box>
                    <SwitchView
                      value={biDashboardState.patientView}
                      onChange={(newView) => {
                        if (newView !== null) {
                          biDashboardState.setPatientView(newView);
                        }
                      }}
                    />
                  </Box>
                  {biDashboardState.patientView === "plot" ? (
                    <Paper className="custom-paper" sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          minHeight: "45px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 4,
                          pt: 0.5,
                          pb: 0,
                          mb: 0,
                        }}
                      >
                        <PlotSelector
                          plotType={biDashboardState.selectPatientPlot}
                          onChange={biDashboardState.setSelectPatientPlot}
                          availableOptions={["new patients", "cumulative patients"]}
                          optionLabels={{
                            "new patients": "Anzahl neuer Patienten",
                            "cumulative patients": "Kumulative Anzahl der Patienten",
                          }}
                        />
                        <LocationSelector
                          name="locations"
                          options={
                            biDashboardState.selectPatientPlot === "new patients"
                              ? availableNewPatientLocations.map((loc) => ({ value: loc.id, label: loc.name }))
                              : availableCumulativePatientLocations.map((loc) => ({ value: loc.id, label: loc.name }))
                          }
                          value={
                            biDashboardState.selectPatientPlot === "new patients"
                              ? biDashboardState.patientLocations_newPatients
                              : biDashboardState.patientLocations_cumulativePatients
                          }
                          onChange={(e) => {
                            if (biDashboardState.selectPatientPlot === "new patients")
                              biDashboardState.setPatientLocations_newPatients(e.target.value);
                            else biDashboardState.setPatientLocations_cumulativePatients(e.target.value);
                          }}
                        />
                        <BinTypeSelector
                          binType={biDashboardState.patientBinType}
                          onChange={biDashboardState.setPatientBinType}
                          availableOptions={["day", "week", "month", "year"]}
                          sx={{ mb: 0, pt: 0.5, pb: 0 }}
                        />
                      </Box>
                      <Box
                        sx={{ position: "relative", height: "400px", display: "flex", justifyContent: "center", alignItems: "center", pt: 0, mt: 0 }}
                      >
                        {biDashboardState.selectPatientPlot === "new patients" && (
                          <>
                            <NewPatientsPlot countData={patientCountResults} />
                            {isNewPatientsLoading && <LoadingSpinner />}
                          </>
                        )}

                        {biDashboardState.selectPatientPlot === "cumulative patients" && (
                          <>
                            <CumulativePatientsPlot cumulativeData={patientCumulativeResults} />
                            {isCumulativePatientsLoading && <LoadingSpinner />}
                          </>
                        )}
                      </Box>
                    </Paper>
                  ) : (
                    <Paper className="paper-container-for-table">
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mr: 2 }}>
                        <TableViewSelector
                          value={biDashboardState.patientTable}
                          onChange={biDashboardState.setPatientTable}
                          isDisabled={biDashboardState.selectPatientPlot === "cumulative patients"}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <BinTypeSelector
                            binType={biDashboardState.patientBinType}
                            onChange={biDashboardState.setPatientBinType}
                            availableOptions={["day", "week", "month", "year"]}
                          />
                          <ExportButton
                            data={patientConfig?.data || []}
                            title={patientConfig?.title || ""}
                            columns={patientConfig?.columns || []}
                            startDate={biDashboardState.patientStartDate}
                            endDate={biDashboardState.patientEndDate}
                            disabled={!patientConfig}
                          />
                        </Box>
                      </Box>
                      {(() => {
                        switch (biDashboardState.selectPatientPlot) {
                          case "new patients":
                            return biDashboardState.patientTable === "Zusammenfassung" ? (
                              <SummaryStatsTable
                                inputData={calculateSummaryStats(patientCountResults, "newPatients")}
                                columns={summaryStatsTableConfigs.counts.columns}
                                isLoading={isNewPatientsLoading}
                              />
                            ) : (
                              <PlotDataTable
                                data={patientCountResults}
                                columns={plotDataTableConfigs.newPatients.columns}
                                isLoading={isNewPatientsLoading}
                              />
                            );

                          case "cumulative patients":
                            return biDashboardState.patientTable === "Alle Daten" ? (
                              <PlotDataTable
                                data={patientCumulativeResults}
                                columns={plotDataTableConfigs.cumulativePatients.columns}
                                isLoading={isCumulativePatientsLoading}
                              />
                            ) : null;
                        }
                      })()}
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card className="custom-card">
                <CardHeader title="Rezept-Statistik" />
                <CardContent sx={{ flexGrow: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", gap: 3 }}>
                      <TimeframePagination
                        onPrevious={() => {
                          const [prevStart, prevEnd] = getPreviousMonth(biDashboardState.rxStartDate);
                          biDashboardState.setRxStartDate(prevStart);
                          biDashboardState.setRxEndDate(prevEnd);
                        }}
                        onNext={() => {
                          const [nextStart, nextEnd] = getNextMonth(biDashboardState.rxStartDate);
                          biDashboardState.setRxStartDate(nextStart);
                          biDashboardState.setRxEndDate(nextEnd);
                        }}
                      />
                      <BIDateRangePicker
                        startDate={biDashboardState.rxStartDate}
                        endDate={biDashboardState.rxEndDate}
                        onChange={({ startDate, endDate }) => {
                          biDashboardState.setRxStartDate(startDate);
                          biDashboardState.setRxEndDate(endDate);
                        }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", gap: 7, alignItems: "center" }}>
                      <RxBlankoSwitch 
                        checked={biDashboardState.rxBlankoFilter} 
                        onChange={biDashboardState.setRxBlankoFilter} 
                      />
                      <SwitchView
                        value={biDashboardState.rxView}
                        onChange={(newView) => {
                          if (newView !== null) {
                            biDashboardState.setRxView(newView);
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  {biDashboardState.rxView === "plot" ? (
                    <Paper className="custom-paper" sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          minHeight: "45px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 4,
                          pt: 0.5,
                          pb: 0,
                          mb: 0,
                        }}
                      >
                        <PlotSelector
                          plotType={biDashboardState.selectRxPlot}
                          onChange={biDashboardState.setSelectRxPlot}
                          availableOptions={["all rx count", "all rx value", "rx therapist count", "rx lifespan distribution"]}
                          optionLabels={{
                            "all rx count": "Anzahl der Verordnungen",
                            "all rx value": "Gesamtwert der Verordnungen",
                            "rx therapist count": "Number of therapists per Rx",
                            "rx lifespan distribution": "Rx Lifespan Distribution",
                          }}
                        />
                        <LocationSelector
                          name="locations"
                          options={(() => {
                            switch (biDashboardState.selectRxPlot) {
                              case "all rx count":
                                return availableRxCountLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                              case "all rx value":
                                return availableRxValueLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                              case "rx therapist count":
                                return availableRxTherapistCountLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                              case "rx lifespan distribution":
                                return availableRxLifespanLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                              default:
                                return availableRxCountLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                            }
                          })()}
                          value={(() => {
                            switch (biDashboardState.selectRxPlot) {
                              case "all rx count":
                                return biDashboardState.rxLocations_allRxCount;
                              case "all rx value":
                                return biDashboardState.rxLocations_allRxValue;
                              case "rx therapist count":
                                return biDashboardState.rxLocations_rxTherapistCount;
                              case "rx lifespan distribution":
                                return biDashboardState.rxLocations_rxLifespanDistribution;
                              default:
                                return biDashboardState.rxLocations_allRxCount;
                            }
                          })()}
                          onChange={(e) => {
                            switch (biDashboardState.selectRxPlot) {
                              case "all rx count":
                                return biDashboardState.setRxLocations_allRxCount(e.target.value);
                              case "all rx value":
                                return biDashboardState.setRxLocations_allRxValue(e.target.value);
                              case "rx therapist count":
                                return biDashboardState.setRxLocations_rxTherapistCount(e.target.value);
                              case "rx lifespan distribution":
                                return biDashboardState.setRxLocations_rxLifespanDistribution(e.target.value);
                              default:
                                return biDashboardState.setRxLocations_allRxCount(e.target.value);
                            }
                          }}
                        />
                        {(() => {
                          if (
                            biDashboardState.selectRxPlot !== "rx therapist count" &&
                            biDashboardState.selectRxPlot !== "rx lifespan distribution"
                          ) {
                            return (
                              <BinTypeSelector
                                binType={biDashboardState.rxBinType}
                                onChange={biDashboardState.setRxBinType}
                                availableOptions={["day", "week", "month", "year"]}
                                sx={{ mb: 0, pt: 0.5, pb: 0 }}
                              />
                            );
                          } else if (biDashboardState.selectRxPlot === "rx lifespan distribution") {
                            return (
                              <RxLifespanTimeDeltaSelector
                                deltaType={biDashboardState.selectRxDeltaType}
                                onChange={biDashboardState.setSelectRxDeltaType}
                              />
                            );
                          } else if (biDashboardState.selectRxPlot === "rx therapist count") {
                            return (
                              <TherapistCountFilter
                                availableCounts={availableTherapistCounts}
                                selectedCounts={biDashboardState.therapistCountFilter}
                                onChange={biDashboardState.setTherapistCountFilter}
                              />
                            );
                          }
                          return null;
                        })()}
                      </Box>
                      <Box
                        sx={{ position: "relative", height: "400px", display: "flex", justifyContent: "center", alignItems: "center", pt: 0, mt: 0 }}
                      >
                        {biDashboardState.selectRxPlot === "all rx count" && (
                          <>
                            <RxCountsPlot countData={totalRxCountResults} />
                            {isAllRxCountLoading && <LoadingSpinner />}
                          </>
                        )}

                        {biDashboardState.selectRxPlot === "all rx value" && (
                          <>
                            <RxValuesPlot rxValueData={totalRxValueResults} />
                            {isAllRxValueLoading && <LoadingSpinner />}
                          </>
                        )}

                        {biDashboardState.selectRxPlot === "rx therapist count" && (
                          <>
                            <RxTherapistCountsPlot countData={filteredRxTherapistsStats} />
                            {isRxTherapistCountLoading && <LoadingSpinner />}
                          </>
                        )}

                        {biDashboardState.selectRxPlot === "rx lifespan distribution" && (
                          <>
                            <RxLifespanPlot rxLifespanData={rxLifespan} deltaType={biDashboardState.selectRxDeltaType} />
                            {isRxLifespanLoading && <LoadingSpinner />}
                          </>
                        )}
                      </Box>
                    </Paper>
                  ) : (
                    <Paper className="paper-container-for-table">
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mr: 2 }}>
                        <TableViewSelector
                          value={biDashboardState.rxTable}
                          onChange={biDashboardState.setRxTable}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          {(() => {
                            if (
                              biDashboardState.selectRxPlot !== "rx therapist count" &&
                              biDashboardState.selectRxPlot !== "rx lifespan distribution"
                            ) {
                              return (
                                <BinTypeSelector
                                  binType={biDashboardState.rxBinType}
                                  onChange={biDashboardState.setRxBinType}
                                  availableOptions={["day", "week", "month", "year"]}
                                />
                              );
                            } else if (biDashboardState.selectRxPlot === "rx therapist count" && biDashboardState.rxTable === "Alle Daten") {
                              return (
                                <TherapistCountFilter
                                  availableCounts={availableTherapistCounts}
                                  selectedCounts={biDashboardState.therapistCountFilter}
                                  onChange={biDashboardState.setTherapistCountFilter}
                                />
                              );
                            } else if (
                              biDashboardState.selectRxPlot === "rx lifespan distribution" &&
                              biDashboardState.rxTable === "Alle Daten"
                            ) {
                              return (
                                <RxLifespanTimeDeltaSelector
                                  deltaType={biDashboardState.selectRxDeltaType}
                                  onChange={biDashboardState.setSelectRxDeltaType}
                                />
                              );
                            }
                            return null;
                          })()}
                          <ExportButton
                            data={rxConfig?.data || []}
                            title={rxConfig?.title || ""}
                            columns={rxConfig?.columns || []}
                            startDate={biDashboardState.rxStartDate}
                            endDate={biDashboardState.rxEndDate}
                            disabled={!rxConfig}
                          />
                        </Box>
                      </Box>
                      {(() => {
                        switch (biDashboardState.selectRxPlot) {
                          case "all rx count":
                            return biDashboardState.rxTable === "Zusammenfassung" ? (
                              <SummaryStatsTable
                                inputData={calculateSummaryStats(totalRxCountResults, "totalCount")}
                                columns={summaryStatsTableConfigs.counts.columns}
                                isLoading={isAllRxCountLoading}
                              />
                            ) : (
                              <PlotDataTable
                                data={totalRxCountResults}
                                columns={plotDataTableConfigs.totalRxCount.columns}
                                isLoading={isAllRxCountLoading}
                              />
                            );

                          case "all rx value":
                            return biDashboardState.rxTable === "Zusammenfassung" ? (
                              <SummaryStatsTable
                                inputData={calculateSummaryStats(totalRxValueResults, "totalRxValue")}
                                columns={summaryStatsTableConfigs.values.columns}
                                isLoading={isAllRxValueLoading}
                              />
                            ) : (
                              <PlotDataTable
                                data={totalRxValueResults}
                                columns={plotDataTableConfigs.totalRxValue.columns}
                                isLoading={isAllRxValueLoading}
                              />
                            );

                          case "rx therapist count":
                            return biDashboardState.rxTable === "Zusammenfassung" ? (
                              <SummaryStatsTable
                                inputData={calculateTherapistPerRxSummaryStats(rxTherapistsStats, "therapistCount")}
                                columns={summaryStatsTableConfigs.therapistsPerRx.columns}
                                isLoading={isRxTherapistCountLoading}
                              />
                            ) : (
                              <PlotDataTable
                                data={transformedRxTherapistsStats}
                                columns={plotDataTableConfigs.rxTherapistCount.columns}
                                isLoading={isRxTherapistCountLoading}
                              />
                            );

                          case "rx lifespan distribution":
                            return biDashboardState.rxTable === "Zusammenfassung" ? (
                              <SummaryStatsTable
                                inputData={calculateRxLifespanSummaryStats(rxLifespan)}
                                columns={summaryStatsTableConfigs.rxLifespan.columns}
                                isLoading={isRxLifespanLoading}
                              />
                            ) : (
                              <PlotDataTable
                                data={transformedRxLifespan}
                                columns={plotDataTableConfigs.rxLifespan.columns}
                                isLoading={isRxLifespanLoading}
                                highlightColumnKey={biDashboardState.selectRxDeltaType}
                              />
                            );
                        }
                      })()}
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card className="custom-card">
                <CardHeader title="Terminplan-Statistik" />
                <CardContent sx={{ flexGrow: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", gap: 3 }}>
                      <TimeframePagination
                        onPrevious={() => {
                          const [prevStart, prevEnd] = getPreviousMonth(biDashboardState.scheduleStartDate);
                          biDashboardState.setScheduleStartDate(prevStart);
                          biDashboardState.setScheduleEndDate(prevEnd);
                        }}
                        onNext={() => {
                          const [nextStart, nextEnd] = getNextMonth(biDashboardState.scheduleStartDate);
                          biDashboardState.setScheduleStartDate(nextStart);
                          biDashboardState.setScheduleEndDate(nextEnd);
                        }}
                      />
                      <BIDateRangePicker
                        startDate={biDashboardState.scheduleStartDate}
                        endDate={biDashboardState.scheduleEndDate}
                        onChange={({ startDate, endDate }) => {
                          biDashboardState.setScheduleStartDate(startDate);
                          biDashboardState.setScheduleEndDate(endDate);
                        }}
                      />
                    </Box>
                    <SwitchView
                      value={biDashboardState.scheduleView}
                      onChange={(newView) => {
                        if (newView !== null) {
                          biDashboardState.setScheduleView(newView);
                        }
                      }}
                    />
                  </Box>
                  {biDashboardState.scheduleView === "plot" ? (
                    <Paper className="custom-paper" sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          minHeight: "45px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 4,
                          pt: 0.7,
                          pb: 0,
                          mb: 0,
                        }}
                      >
                        <PlotSelector
                          plotType={biDashboardState.selectScheduleBasedPlot}
                          onChange={biDashboardState.setSelectScheduleBasedPlot}
                          availableOptions={["total appointment value", "all rx count", "total gaps", "therapyRx frequency"]}
                          optionLabels={{
                            "total appointment value": "Gesamtwert der Termine",
                            "all rx count": "Anzahl der Verordnungen",
                            "total gaps": "Gesamte Lücken",
                            "therapyRx frequency": "TherapyRx frequency",
                          }}
                        />
                        <LocationSelector
                          name="locations"
                          options={(() => {
                            switch (biDashboardState.selectScheduleBasedPlot) {
                              case "total appointment value":
                                return availableApptValueLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                              case "all rx count":
                                return availableScheduleRxCountLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                              case "total gaps":
                                return availableGapsLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                              case "therapyRx frequency":
                                return availableTherapyRxFrequencyLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                              default:
                                return availableApptValueLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                            }
                          })()}
                          value={(() => {
                            switch (biDashboardState.selectScheduleBasedPlot) {
                              case "total appointment value":
                                return biDashboardState.scheduleLocations_totalAppointmentValue;
                              case "all rx count":
                                return biDashboardState.scheduleLocations_allRxCount;
                              case "total gaps":
                                return biDashboardState.scheduleLocations_totalGaps;
                              case "therapyRx frequency":
                                return biDashboardState.scheduleLocations_therapyRxFrequency;
                              default:
                                return biDashboardState.scheduleLocations_totalAppointmentValue;
                            }
                          })()}
                          onChange={(e) => {
                            switch (biDashboardState.selectScheduleBasedPlot) {
                              case "total appointment value":
                                return biDashboardState.setScheduleLocations_totalAppointmentValue(e.target.value);
                              case "all rx count":
                                return biDashboardState.setScheduleLocations_allRxCount(e.target.value);
                              case "total gaps":
                                return biDashboardState.setScheduleLocations_totalGaps(e.target.value);
                              case "therapyRx frequency":
                                return biDashboardState.setScheduleLocations_therapyRxFrequency(e.target.value);
                              default:
                                return biDashboardState.setScheduleLocations_totalAppointmentValue(e.target.value);
                            }
                          }}
                        />
                        {biDashboardState.selectScheduleBasedPlot !== "therapyRx frequency" && (
                          <BinTypeSelector
                            binType={biDashboardState.scheduleBinType}
                            onChange={biDashboardState.setScheduleBinType}
                            availableOptions={["day", "week", "month", "year"]}
                            sx={{ mb: 0, pt: 0.5, pb: 0 }}
                          />
                        )}
                      </Box>
                      <Box sx={{ position: "relative", height: "400px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        {biDashboardState.selectScheduleBasedPlot === "total appointment value" && (
                          <>
                            <ScheduleBasedApptValuesPlot scheduleValueData={totalApptValueResults} />
                            {isTotalApptValueLoading && <LoadingSpinner />}
                          </>
                        )}

                        {biDashboardState.selectScheduleBasedPlot === "all rx count" && (
                          <>
                            <RxCountsPlot countData={totalScheduleBasedRxCountResults} />
                            {isScheduleAllRxCountLoading && <LoadingSpinner />}
                          </>
                        )}

                        {biDashboardState.selectScheduleBasedPlot === "total gaps" && (
                          <>
                            <ScheduleBasedGapsPlot scheduleGapData={totalGapsResults} />
                            {isTotalGapsLoading && <LoadingSpinner />}
                          </>
                        )}

                        {biDashboardState.selectScheduleBasedPlot === "therapyRx frequency" && (
                          <>
                            <ScheduleBasedTherapyRxPlot freqData={therapyRxFrequency} />
                            {isTherapyRxFrequencyLoading && <LoadingSpinner />}
                          </>
                        )}
                      </Box>
                    </Paper>
                  ) : (
                    <Paper className="paper-container-for-table">
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mr: 2 }}>
                        <TableViewSelector
                          value={biDashboardState.scheduleTable}
                          onChange={biDashboardState.setScheduleTable}
                          isDisabled={biDashboardState.selectScheduleBasedPlot === "therapyRx frequency"}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          {biDashboardState.selectScheduleBasedPlot !== "therapyRx frequency" && (
                            <BinTypeSelector
                              binType={biDashboardState.scheduleBinType}
                              onChange={biDashboardState.setScheduleBinType}
                              availableOptions={["day", "week", "month", "year"]}
                            />
                          )}
                          <ExportButton
                            data={scheduleConfig?.data || []}
                            title={scheduleConfig?.title || ""}
                            columns={scheduleConfig?.columns || []}
                            startDate={biDashboardState.scheduleStartDate}
                            endDate={biDashboardState.scheduleEndDate}
                            disabled={!scheduleConfig}
                          />
                        </Box>
                      </Box>
                      {(() => {
                        switch (biDashboardState.selectScheduleBasedPlot) {
                          case "total appointment value":
                            return biDashboardState.scheduleTable === "Zusammenfassung" ? (
                              <SummaryStatsTable
                                inputData={calculateSummaryStats(totalApptValueResults, "totalValue")}
                                columns={summaryStatsTableConfigs.values.columns}
                                isLoading={isTotalApptValueLoading}
                              />
                            ) : (
                              <PlotDataTable
                                data={totalApptValueResults}
                                columns={plotDataTableConfigs.totalApptValue.columns}
                                isLoading={isTotalApptValueLoading}
                              />
                            );

                          case "all rx count":
                            return biDashboardState.scheduleTable === "Zusammenfassung" ? (
                              <SummaryStatsTable
                                inputData={calculateSummaryStats(totalScheduleBasedRxCountResults, "totalCount")}
                                columns={summaryStatsTableConfigs.counts.columns}
                                isLoading={isScheduleAllRxCountLoading}
                              />
                            ) : (
                              <PlotDataTable
                                data={totalScheduleBasedRxCountResults}
                                columns={plotDataTableConfigs.totalRxCount.columns}
                                isLoading={isScheduleAllRxCountLoading}
                              />
                            );

                          case "total gaps":
                            return biDashboardState.scheduleTable === "Zusammenfassung" ? (
                              <SummaryStatsTable
                                inputData={calculateSummaryStats(totalGapsResults, "totalGaps")}
                                columns={summaryStatsTableConfigs.counts.columns}
                                isLoading={isTotalGapsLoading}
                              />
                            ) : (
                              <PlotDataTable
                                data={totalGapsResults}
                                columns={plotDataTableConfigs.totalGaps.columns}
                                isLoading={isTotalGapsLoading}
                              />
                            );

                          case "therapyRx frequency":
                            return biDashboardState.scheduleTable === "Alle Daten" ? (
                              <PlotDataTable
                                data={therapyRxFrequency}
                                columns={plotDataTableConfigs.therapyRxFrequency.columns}
                                isLoading={isTherapyRxFrequencyLoading}
                              />
                            ) : null;
                        }
                      })()}
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card className="custom-card">
                <CardHeader title="Geschätzter Monatsumsatz" />
                <CardContent sx={{ flexGrow: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <TimeframePagination
                      onPrevious={() => {
                        const [prevStart] = getPreviousMonth(biDashboardState.revenueMonth);
                        biDashboardState.setRevenueMonth(prevStart);
                      }}
                      onNext={() => {
                        const [nextStart] = getNextMonth(biDashboardState.revenueMonth);
                        biDashboardState.setRevenueMonth(nextStart);
                      }}
                    />
                    <SwitchView
                      value={biDashboardState.monthlyRevenueView}
                      onChange={(newView) => {
                        if (newView !== null) {
                          biDashboardState.setMonthlyRevenueView(newView);
                        }
                      }}
                    />
                  </Box>
                  {biDashboardState.monthlyRevenueView === "plot" ? (
                    <Paper className="custom-paper" sx={{ mt: 2 }}>
                      <Box
                        sx={{ minHeight: "45px", display: "flex", justifyContent: "flex-end", alignItems: "center", px: 4, pt: 0.5, pb: 0, mb: 0 }}
                      >
                        <LocationSelector
                          name="locations"
                          options={availableMonthlyRevenueLocations.map((loc) => ({ value: loc.id, label: loc.name }))}
                          value={biDashboardState.monthlyRevenueLocations}
                          onChange={(e) => biDashboardState.setMonthlyRevenueLocations(e.target.value)}
                        />
                      </Box>
                      <Box sx={{ position: "relative", height: "400px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <>
                          <MonthlyRevenuePlot monthlyRevenueData={monthlyRevenue} />
                          {isMonthlyRevenueLoading && <LoadingSpinner />}
                        </>
                      </Box>
                    </Paper>
                  ) : (
                    <Paper className="paper-container-for-table">
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mr: 2 }}>
                        <TableViewSelector
                          value={biDashboardState.monthlyRevenueTable}
                          onChange={biDashboardState.setMonthlyRevenueTable}
                          isDisabled={true}
                        />
                        <ExportButton
                          data={revenueConfig?.data || []}
                          title={revenueConfig?.title || ""}
                          columns={revenueConfig?.columns || []}
                          startDate={biDashboardState.revenueMonth}
                          endDate={new Date(biDashboardState.revenueMonth.getFullYear(), biDashboardState.revenueMonth.getMonth() + 1, 0)}
                          disabled={!revenueConfig}
                        />
                      </Box>
                      <PlotDataTable
                        data={monthlyRevenue}
                        columns={plotDataTableConfigs.estimatedMonthlyRevenue.columns}
                        isLoading={isMonthlyRevenueLoading}
                      />
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card className="custom-card">
                <CardHeader title="Blankoverordnung-Statistik" />
                <CardContent sx={{ flexGrow: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", gap: 3 }}>
                      <TimeframePagination
                        onPrevious={() => {
                          const [prevStart, prevEnd] = getPreviousMonth(biDashboardState.rxBlankoStartDate);
                          biDashboardState.setRxBlankoStartDate(prevStart);
                          biDashboardState.setRxBlankoEndDate(prevEnd);
                        }}
                        onNext={() => {
                          const [nextStart, nextEnd] = getNextMonth(biDashboardState.rxBlankoStartDate);
                          biDashboardState.setRxBlankoStartDate(nextStart);
                          biDashboardState.setRxBlankoEndDate(nextEnd);
                        }}
                      />
                      <BIDateRangePicker
                        startDate={biDashboardState.rxBlankoStartDate}
                        endDate={biDashboardState.rxBlankoEndDate}
                        onChange={({ startDate, endDate }) => {
                          biDashboardState.setRxBlankoStartDate(startDate);
                          biDashboardState.setRxBlankoEndDate(endDate);
                        }}
                      />
                    </Box>
                    <SwitchView
                      value={biDashboardState.rxBlankoView}
                      onChange={(newView) => {
                        if (newView !== null) {
                          biDashboardState.setRxBlankoView(newView);
                        }
                      }}
                    />
                  </Box>
                  {biDashboardState.rxBlankoView === "plot" ? (
                    <Paper className="custom-paper" sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          minHeight: "45px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 4,
                          pt: 0.5,
                          pb: 0,
                          mb: 0,
                        }}
                      >
                        <PlotSelector
                          plotType={biDashboardState.selectRxBlankoPlot}
                          onChange={biDashboardState.setSelectRxBlankoPlot}
                          availableOptions={[
                            "all rx blanko count",
                            "all rx blanko value",
                            "rx blanko therapist count",
                            "rx blanko lifespan distribution",
                          ]}
                          optionLabels={{
                            "all rx blanko count": "Anzahl der Verordnungen",
                            "all rx blanko value": "Gesamtwert der Verordnungen",
                            "rx blanko therapist count": "Number of therapists per Rx",
                            "rx blanko lifespan distribution": "Rx Lifespan Distribution",
                          }}
                        />
                        <LocationSelector
                          name="locations"
                          options={(() => {
                            switch (biDashboardState.selectRxBlankoPlot) {
                              case "all rx blanko count":
                                return availableRxBlankoCountLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                              case "all rx blanko value":
                                return availableRxBlankoValueLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                              case "rx blanko therapist count":
                                return availableRxBlankoTherapistCountLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                              case "rx blanko lifespan distribution":
                                return availableRxBlankoLifespanLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                              default:
                                return availableRxBlankoCountLocations.map((loc) => ({ value: loc.id, label: loc.name }));
                            }
                          })()}
                          value={(() => {
                            switch (biDashboardState.selectRxBlankoPlot) {
                              case "all rx blanko count":
                                return biDashboardState.rxBlankoLocations_allRxBlankoCount;
                              case "all rx blanko value":
                                return biDashboardState.rxBlankoLocations_allRxBlankoValue;
                              case "rx blanko therapist count":
                                return biDashboardState.rxBlankoLocations_rxBlankoTherapistCount;
                              case "rx blanko lifespan distribution":
                                return biDashboardState.rxBlankoLocations_rxBlankoLifespanDistribution;
                              default:
                                return biDashboardState.rxBlankoLocations_allRxBlankoCount;
                            }
                          })()}
                          onChange={(e) => {
                            switch (biDashboardState.selectRxBlankoPlot) {
                              case "all rx blanko count":
                                return biDashboardState.setRxBlankoLocations_allRxBlankoCount(e.target.value);
                              case "all rx blanko value":
                                return biDashboardState.setRxBlankoLocations_allRxBlankoValue(e.target.value);
                              case "rx blanko therapist count":
                                return biDashboardState.setRxBlankoLocations_rxBlankoTherapistCount(e.target.value);
                              case "rx blanko lifespan distribution":
                                return biDashboardState.setRxBlankoLocations_rxBlankoLifespanDistribution(e.target.value);
                              default:
                                return biDashboardState.setRxBlankoLocations_allRxBlankoCount(e.target.value);
                            }
                          }}
                        />
                        {(() => {
                          if (
                            biDashboardState.selectRxBlankoPlot !== "rx blanko therapist count" &&
                            biDashboardState.selectRxBlankoPlot !== "rx blanko lifespan distribution"
                          ) {
                            return (
                              <BinTypeSelector
                                binType={biDashboardState.rxBlankoBinType}
                                onChange={biDashboardState.setRxBlankoBinType}
                                availableOptions={["day", "week", "month", "year"]}
                                sx={{ mb: 0, pt: 0.5, pb: 0 }}
                              />
                            );
                          } else if (biDashboardState.selectRxBlankoPlot === "rx blanko therapist count") {
                            return (
                              <TherapistCountFilter
                                availableCounts={availableRxBlankoTherapistCounts}
                                selectedCounts={biDashboardState.therapistCountFilter}
                                onChange={biDashboardState.setTherapistCountFilter}
                              />
                            );
                          } else if (biDashboardState.selectRxBlankoPlot === "rx blanko lifespan distribution") {
                            return (
                              <RxLifespanTimeDeltaSelector
                                deltaType={biDashboardState.selectRxBlankoDeltaType}
                                onChange={biDashboardState.setSelectRxBlankoDeltaType}
                              />
                            );
                          }
                          return null;
                        })()}
                      </Box>
                      <Box
                        sx={{ position: "relative", height: "400px", display: "flex", justifyContent: "center", alignItems: "center", pt: 0, mt: 0 }}
                      >
                        {biDashboardState.selectRxBlankoPlot === "all rx blanko count" && (
                          <>
                            <RxCountsPlot countData={totalRxBlankoCountResults} />
                            {isRxBlankoCountsLoading && <LoadingSpinner />}
                          </>
                        )}

                        {biDashboardState.selectRxBlankoPlot === "all rx blanko value" && (
                          <>
                            <RxValuesPlot rxValueData={totalRxBlankoValueResults} />
                            {isRxBlankoValuesLoading && <LoadingSpinner />}
                          </>
                        )}

                        {biDashboardState.selectRxBlankoPlot === "rx blanko therapist count" && (
                          <>
                            <RxTherapistCountsPlot countData={filteredRxBlankoWithTherapists} />
                            {isRxBlankoTherapistLoading && <LoadingSpinner />}
                          </>
                        )}

                        {biDashboardState.selectRxBlankoPlot === "rx blanko lifespan distribution" && (
                          <>
                            <RxLifespanPlot rxLifespanData={rxBlankoLifespan} deltaType={biDashboardState.selectRxBlankoDeltaType} />
                            {isRxBlankoLifespanLoading && <LoadingSpinner />}
                          </>
                        )}
                      </Box>
                    </Paper>
                  ) : (
                    <Paper className="paper-container-for-table">
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mr: 2 }}>
                        <TableViewSelector 
                          value={biDashboardState.rxBlankoTable} 
                          onChange={biDashboardState.setRxBlankoTable} 
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          {(() => {
                            if (
                              biDashboardState.selectRxBlankoPlot !== "rx blanko therapist count" &&
                              biDashboardState.selectRxBlankoPlot !== "rx blanko lifespan distribution"
                            ) {
                              return (
                                <BinTypeSelector
                                  binType={biDashboardState.rxBlankoBinType}
                                  onChange={biDashboardState.setRxBlankoBinType}
                                  availableOptions={["day", "week", "month", "year"]}
                                />
                              );
                            } else if (
                              biDashboardState.selectRxBlankoPlot === "rx blanko therapist count" &&
                              biDashboardState.rxBlankoTable === "Alle Daten"
                            ) {
                              return (
                                <TherapistCountFilter
                                  availableCounts={availableRxBlankoTherapistCounts}
                                  selectedCounts={biDashboardState.therapistCountFilter}
                                  onChange={biDashboardState.setTherapistCountFilter}
                                />
                              );
                            } else if (
                              biDashboardState.selectRxBlankoPlot === "rx blanko lifespan distribution" &&
                              biDashboardState.rxBlankoTable === "Alle Daten"
                            ) {
                              return (
                                <RxLifespanTimeDeltaSelector
                                  deltaType={biDashboardState.selectRxBlankoDeltaType}
                                  onChange={biDashboardState.setSelectRxBlankoDeltaType}
                                />
                              );
                            }
                            return null;
                          })()}
                          <ExportButton
                            data={rxBlankoConfig?.data || []}
                            title={rxBlankoConfig?.title || ""}
                            columns={rxBlankoConfig?.columns || []}
                            startDate={biDashboardState.rxBlankoStartDate}
                            endDate={biDashboardState.rxBlankoEndDate}
                            disabled={!rxBlankoConfig}
                          />
                        </Box>
                      </Box>
                      {(() => {
                        switch (biDashboardState.selectRxBlankoPlot) {
                          case "all rx blanko count":
                            return biDashboardState.rxBlankoTable === "Zusammenfassung" ? (
                              <SummaryStatsTable
                                inputData={calculateSummaryStats(totalRxBlankoCountResults, "totalCount")}
                                columns={summaryStatsTableConfigs.counts.columns}
                                isLoading={isRxBlankoCountsLoading}
                              />
                            ) : (
                              <PlotDataTable
                                data={totalRxBlankoCountResults}
                                columns={plotDataTableConfigs.totalRxBlankoCount.columns}
                                isLoading={isRxBlankoCountsLoading}
                              />
                            );

                          case "all rx blanko value":
                            return biDashboardState.rxBlankoTable === "Zusammenfassung" ? (
                              <SummaryStatsTable
                                inputData={calculateSummaryStats(totalRxBlankoValueResults, "totalRxValue")}
                                columns={summaryStatsTableConfigs.values.columns}
                                isLoading={isRxBlankoValuesLoading}
                              />
                            ) : (
                              <PlotDataTable
                                data={totalRxBlankoValueResults}
                                columns={plotDataTableConfigs.totalRxBlankoValue.columns}
                                isLoading={isRxBlankoValuesLoading}
                              />
                            );

                          case "rx blanko therapist count":
                            return biDashboardState.rxBlankoTable === "Zusammenfassung" ? (
                              <SummaryStatsTable
                                inputData={calculateTherapistPerRxSummaryStats(filteredRxBlankoWithTherapists, "therapistCount")}
                                columns={summaryStatsTableConfigs.therapistsPerRx.columns}
                                isLoading={isRxBlankoTherapistLoading}
                              />
                            ) : (
                              <PlotDataTable
                                data={transformedRxBlankoTherapistsStats}
                                columns={plotDataTableConfigs.rxBlankoTherapistCount.columns}
                                isLoading={isRxBlankoTherapistLoading}
                              />
                            );

                          case "rx blanko lifespan distribution":
                            return biDashboardState.rxBlankoTable === "Zusammenfassung" ? (
                              <SummaryStatsTable
                                inputData={calculateRxLifespanSummaryStats(rxBlankoLifespan)}
                                columns={summaryStatsTableConfigs.rxLifespan.columns}
                                isLoading={isRxBlankoLifespanLoading}
                              />
                            ) : (
                              <PlotDataTable
                                data={transformedRxBlankoLifespan}
                                columns={plotDataTableConfigs.rxBlankoLifespan.columns}
                                isLoading={isRxBlankoLifespanLoading}
                                highlightColumnKey={biDashboardState.selectRxBlankoDeltaType}
                              />
                            );
                        }
                      })()}
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card className="custom-card">
                <CardHeader title="Jahresend-Rezeptwerte" />
                <CardContent sx={{ flexGrow: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 0 }}>
                    <Box sx={{ display: "flex", gap: 3 }}>
                      <TimeframePagination
                        onPrevious={() => {
                          const [prevStart, prevEnd] = getPreviousMonth(biDashboardState.yearEndRxUnfinishedValuesStartDate, biDashboardState.yearEndRxUnfinishedValuesEndDate);
                          biDashboardState.setYearEndRxUnfinishedValuesStartDate(prevStart);
                          biDashboardState.setYearEndRxUnfinishedValuesEndDate(prevEnd);
                        }}
                        onNext={() => {
                          const [nextStart, nextEnd] = getNextMonth(biDashboardState.yearEndRxUnfinishedValuesStartDate, biDashboardState.yearEndRxUnfinishedValuesEndDate);
                          biDashboardState.setYearEndRxUnfinishedValuesStartDate(nextStart);
                          biDashboardState.setYearEndRxUnfinishedValuesEndDate(nextEnd);
                        }}
                      />
                      <BIDateRangePicker
                        startDate={biDashboardState.yearEndRxUnfinishedValuesStartDate}
                        endDate={biDashboardState.yearEndRxUnfinishedValuesEndDate}
                        onChange={({ startDate, endDate }) => {
                          biDashboardState.setYearEndRxUnfinishedValuesStartDate(startDate);
                          biDashboardState.setYearEndRxUnfinishedValuesEndDate(endDate);
                        }}
                      />
                    </Box>
                    <ExportButton 
                      data={yearEndUnfinishedValsConfig.data}
                      columns={yearEndUnfinishedValsConfig.columns}
                      title={yearEndUnfinishedValsConfig.title}
                      startDate={biDashboardState.yearEndRxUnfinishedValuesStartDate}
                      endDate={biDashboardState.yearEndRxUnfinishedValuesEndDate}
                      // year={biDashboardState.yearEndRxUnfinishedValuesYear}
                    />
                  </Box>
                  <Paper className="paper-container-for-table" sx={{ pt: 0, mt: 0 }}>
                    <YearEndRxUnfinishedValueTable yearEndRxData={yearEndRxUnfinishedValues} columns={yearEndRxUnfinishedValuesTableConfigs.yearEndRxUnfinishedValues.columns} isLoading={isYearEndRxUnfinishedValuesLoading} />
                  </Paper>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
        {selectedTab === 1 && <InsuranceAnalyticsContainer />}
      </Box>
    </Box>
  );
};
